import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Banner, Button, Card, Spinner } from "@magarac/ui";
import { isApiError } from "@magarac/shared/errors";
import { ContactSupport } from "../../../components/help/ContactSupport";
import { GuideMarkdown } from "../../../components/help/GuideMarkdown";
import { messageFor } from "../../../lib/errors";
import { trackHelp, useGuide } from "../../../lib/help";

export const Route = createFileRoute("/_app/help/$id")({
  component: GuidePage,
});

/** One guide in the help center (LC-1), with "did this help" and contact us. */
function GuidePage() {
  const { id } = Route.useParams();
  const q = useGuide(id);
  const [answer, setAnswer] = useState<boolean | null>(null);
  const [contact, setContact] = useState(false);
  useEffect(() => {
    trackHelp({ kind: "view", guideId: id });
  }, [id]);

  return (
    <div className="mx-auto max-w-2xl">
      <nav className="mb-4 text-sm" aria-label="Breadcrumb">
        <Link to="/help" className="text-brand-700 underline">
          Help
        </Link>
        {q.data ? <span className="text-muted"> / {q.data.guide.categoryName}</span> : null}
      </nav>
      {q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <Card>
          <h1 className="mb-2 text-xl font-semibold">
            {isApiError(q.error) && q.error.status === 404
              ? "We could not find that guide"
              : "This guide did not load"}
          </h1>
          <p className="mb-3">
            {isApiError(q.error) && q.error.status === 404
              ? "It may have been renamed."
              : messageFor(q.error)}
          </p>
          <Link to="/help" className="text-brand-700 underline">
            Search all help
          </Link>
        </Card>
      ) : (
        <article>
          <h1 className="mb-4 text-2xl font-semibold">{q.data.guide.title}</h1>
          <GuideMarkdown body={q.data.guide.body} />
          <div className="mt-8 rounded-lg border border-line p-4">
            {answer === null ? (
              <div className="flex flex-wrap items-center gap-2">
                <span>Did this help?</span>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setAnswer(true);
                    trackHelp({ kind: "helpful", guideId: id, helpful: true });
                  }}
                >
                  Yes
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setAnswer(false);
                    setContact(true);
                    trackHelp({ kind: "helpful", guideId: id, helpful: false });
                  }}
                >
                  No
                </Button>
              </div>
            ) : (
              <p>
                {answer
                  ? "Thanks for letting us know."
                  : "Sorry about that. Tell us what you were trying to do."}
              </p>
            )}
          </div>
          {contact ? (
            <Card title="Contact us" className="mt-4">
              <ContactSupport screen={`guide:${id}`} onDone={() => setContact(false)} />
            </Card>
          ) : (
            <p className="mt-4 text-sm">
              Still stuck?{" "}
              <button
                type="button"
                className="text-brand-700 underline"
                onClick={() => setContact(true)}
              >
                Contact us
              </button>
            </p>
          )}
          {!contact && answer === false ? (
            <Banner tone="warn">We read every message.</Banner>
          ) : null}
        </article>
      )}
    </div>
  );
}
